/**
 * Main Dashboard View - System overview with real-time status
 */

import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Activity,
  Zap,
  Server,
  Database,
  Shield,
  Clock,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { useDashboardStore } from "@/store";
import { BackendStatusPanel, BackendStatusIndicator } from "@/components/BackendStatusPanel";
import { ModeSwitcher, ModeBadge } from "@/components/ModeSwitcher";
import { cn } from "@/lib/utils";

export function DashboardView() {
  const { healthStats, addLogEntry, systemMode, authLevel } = useDashboardStore();

  // Log dashboard view load
  useEffect(() => {
    addLogEntry({
      category: "navigation",
      action: "Dashboard Viewed",
      details: `Viewing main dashboard in ${systemMode} mode`,
      severity: "info",
      source: "dashboard-view",
    });
  }, []);

  const stats = [
    {
      label: "Backends Online",
      value: healthStats?.connected || 0,
      total: healthStats?.total || 4,
      color: "text-green-500",
      icon: CheckCircle,
    },
    {
      label: "AGI Requests Today",
      value: 147,
      trend: "+12%",
      color: "text-blue-500",
      icon: Activity,
    },
    {
      label: "System Uptime",
      value: "14d 6h",
      color: "text-purple-500",
      icon: Clock,
    },
    {
      label: "Security Events",
      value: 3,
      alert: true,
      color: "text-orange-500",
      icon: AlertTriangle,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">System overview and status</p>
        </div>
        <div className="flex items-center gap-3">
          <BackendStatusIndicator />
          <ModeSwitcher />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, idx) => {
          const Icon = stat.icon;
          const isAlert = stat.alert;

          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={cn(
                "rounded-xl border p-4",
                isAlert
                  ? "border-[hsl(var(--destructive))] bg-[hsl(var(--destructive)/.1)]"
                  : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"
              )}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{stat.label}</div>
                  <div className="mt-2 text-2xl font-bold">{stat.value}</div>
                  {stat.trend && (
                    <div className="text-xs text-green-500">{stat.trend}</div>
                  )}
                  {stat.total && (
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {stat.value} / {stat.total}
                    </div>
                  )}
                </div>
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-lg",
                    isAlert ? "bg-[hsl(var(--destructive)/.2)]" : "bg-[hsl(var(--muted))]"
                  )}
                >
                  <Icon className={cn("h-5 w-5", stat.color)} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Backend Status */}
      <BackendStatusPanel />

      {/* Mode & Auth Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Mode Info */}
        <motion.div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--primary))]">
              <Server className="h-5 w-5 text-black" />
            </div>
            <div>
              <div className="font-semibold">System Mode</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Current operational mode</div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <ModeBadge />
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              {systemMode === "centralized" && "Using local kernel"}
              {systemMode === "decentralized" && "Using Convex cloud"}
              {systemMode === "hybrid" && "Auto-switching between backends"}
            </div>
          </div>
        </motion.div>

        {/* Auth Status */}
        <motion.div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-lg",
                authLevel === "creator"
                  ? "bg-purple-500/20"
                  : authLevel === "master"
                    ? "bg-blue-500/20"
                    : "bg-[hsl(var(--muted))]"
              )}
            >
              <Shield
                className={cn(
                  "h-5 w-5",
                  authLevel === "creator" ? "text-purple-400" : authLevel === "master" ? "text-blue-400" : "text-[hsl(var(--muted-foreground))]"
                )}
              />
            </div>
            <div>
              <div className="font-semibold">Authorization</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">Current access level</div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div
              className={cn(
                "rounded-lg px-3 py-1.5 text-xs font-medium",
                authLevel === "creator"
                  ? "bg-purple-500/20 text-purple-300"
                  : authLevel === "master"
                    ? "bg-blue-500/20 text-blue-300"
                    : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
              )}
            >
              {authLevel === "none" ? "Not Authenticated" : authLevel === "passkey" ? "Passkey Auth" : `${authLevel} Access`}
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              {authLevel !== "none" ? "Full access enabled" : "Limited capabilities"}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
        <div className="mb-4">
          <div className="font-semibold">Quick Actions</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Common system operations</div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <QuickActionButton
            icon={Activity}
            label="Check Health"
            description="Run health checks"
            onClick={() => addLogEntry({ category: "system", action: "Health Check", details: "Manual health check triggered", severity: "info", source: "dashboard" })}
          />
          <QuickActionButton
            icon={Database}
            label="View Data"
            description="Browse system data"
            onClick={() => { /* Would navigate to data view */ }}
          />
          <QuickActionButton
            icon={Zap}
            label="AGI Access"
            description="Access AI assistant"
            onClick={() => {/* Would open AGI panel */ }}
          />
          <QuickActionButton
            icon={Shield}
            label="Security"
            description="Manage security"
            onClick={() => {/* Would navigate to security view */ }}
          />
        </div>
      </motion.div>
    </div>
  );
}

function QuickActionButton({ icon: Icon, label, description, onClick }: { icon: any; label: string; description: string; onClick: () => void }) {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="flex flex-col gap-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/.3)] p-3 transition-colors hover:bg-[hsl(var(--primary)/.1)] cursor-pointer text-left"
    >
      <Icon className="h-5 w-5 text-[hsl(var(--primary))]" />
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">{description}</div>
      </div>
    </motion.button>
  );
}
